import { create } from 'zustand';
import {
  ProgressSchema,
  trainingKey,
  type Progress,
  type TrainingRecord,
} from '@il-colpo/shared/progress';

export const PROGRESS_STORAGE_KEY = 'ilcolpo.progress';

export function loadProgress(): Progress {
  try {
    const raw = localStorage.getItem(PROGRESS_STORAGE_KEY);
    if (raw) {
      const parsed = ProgressSchema.safeParse(JSON.parse(raw));
      if (parsed.success) return parsed.data;
    }
  } catch {
    // Storage unavailable or corrupt: start from nothing.
  }
  return ProgressSchema.parse({});
}

function saveProgress(progress: Progress): void {
  try {
    localStorage.setItem(PROGRESS_STORAGE_KEY, JSON.stringify(progress));
  } catch {
    // Storage unavailable: progress lasts for this session only.
  }
}

interface ProgressState {
  progress: Progress;
  /** Notes a finished training; a better time or more stars replaces the record. */
  recordTraining: (
    type: string,
    level: number,
    result: { ms: number; stars: 0 | 1 | 2 | 3 },
  ) => void;
  completeChapter: (chapter: number) => void;
  /** Replaces the progress as a whole (what cross-save brings in). */
  setProgress: (progress: Progress) => void;
  reset: () => void;
}

export const useProgressStore = create<ProgressState>((set) => ({
  progress: loadProgress(),
  recordTraining: (type, level, result) => {
    set((state) => {
      if (result.stars === 0) return state;
      const key = trainingKey(type, level);
      const old: TrainingRecord | undefined = state.progress.training[key];
      const next: TrainingRecord = {
        bestMs: old ? Math.min(old.bestMs, result.ms) : result.ms,
        stars: Math.max(old?.stars ?? 1, result.stars),
        plays: (old?.plays ?? 0) + 1,
      };
      const progress = { ...state.progress, training: { ...state.progress.training, [key]: next } };
      saveProgress(progress);
      return { progress };
    });
  },
  completeChapter: (chapter) => {
    set((state) => {
      const progress = ProgressSchema.parse({
        ...state.progress,
        tutorial: [...state.progress.tutorial, chapter],
      });
      saveProgress(progress);
      return { progress };
    });
  },
  setProgress: (progress) => {
    const next = ProgressSchema.parse(progress);
    saveProgress(next);
    set({ progress: next });
  },
  reset: () => {
    const progress = ProgressSchema.parse({});
    saveProgress(progress);
    set({ progress });
  },
}));
